import React from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TextareaFieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  error?: string
  hint?: string
  rows?: number
  maxLength?: number
  showCharCount?: boolean
  className?: string
}

export const TextareaField: React.FC<TextareaFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error,
  hint,
  rows = 4,
  maxLength,
  showCharCount = false,
  className = '',
}) => {
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {showCharCount && maxLength && (
          <span className="text-xs text-muted-foreground">
            {value.length}/{maxLength}
          </span>
        )}
      </div>

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={cn(
          'w-full px-3 py-2 border rounded-md shadow-sm resize-none',
          'bg-card text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent',
          'disabled:bg-surface disabled:cursor-not-allowed disabled:opacity-50',
          'text-sm transition-colors',
          error
            ? 'border-red-500 ring-2 ring-red-200'
            : 'border-border',
        )}
      />

      {hint && !error && (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      )}

      {error && (
        <div className="mt-1 flex items-center text-xs text-red-600">
          <AlertCircle className="h-3 w-3 mr-1" />
          {error}
        </div>
      )}
    </div>
  )
}
